import dayjs from './dayjs'

export const formatPublishedAt = (publishedAt: string) => {
  // Parse as UTC then convert to local timezone for display
  const date = dayjs.utc(publishedAt).local().toDate()
  const now = new Date()
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const lastWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7)

  const timeFormatter = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  if (date.toDateString() === now.toDateString()) {
    return timeFormatter.format(date)
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Hier'
  } else if (date > lastWeek) {
    return date.toLocaleDateString('fr-FR', { weekday: 'long' })
  } else {
    return dateFormatter.format(date)
  }
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days >= 1) return `${days} j`
  if (hours >= 1) return `${hours} h`
  if (minutes >= 1) return `${minutes} min`
  return `${Math.floor(seconds)} s`
}


export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('fr-FR', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

export const today = dayjs().startOf('day');
export const tomorrow = today.add(1, 'day');

export type MomentGroup = 'today' | 'week' | 'month' | 'later';

/**
 * Groups items by moment (today, week, month, later) based on their date field.
 * 
 * @param items - Array of items that have a date field
 * @param dateField - The name of the date field to use. Must be one of:
 *   - 'scheduledDate' for ScheduledMessages
 *   - 'date' for KeepContacts
 * @returns Object with items grouped by moment
 */
export function groupByMoment<T extends { scheduledDate?: string; keepDate?: string; }>(
  items: T[],
  dateField: 'scheduledDate' | 'keepDate' = 'scheduledDate'
): Record<MomentGroup, T[]> {
  const todayItems: T[] = [];
  const weekItems: T[] = [];
  const monthItems: T[] = [];
  const laterItems: T[] = [];

  items.forEach((item) => {
    // Get the date value from the specified field name
    const dateStr = item[dateField];
    if (!dateStr) return;

    const itemDate = dayjs.utc(dateStr).local();
    const todayDate = dayjs().startOf('day');
    const endOfWeek = todayDate.endOf('week');
    const endOfMonth = todayDate.endOf('month');

    if (itemDate.isSame(todayDate, 'day')) {
      todayItems.push(item);
    } else if (itemDate.isAfter(todayDate) && itemDate.isBefore(endOfWeek) || itemDate.isSame(endOfWeek, 'day')) {
      weekItems.push(item);
    } else if (itemDate.isAfter(endOfWeek) && itemDate.isBefore(endOfMonth) || itemDate.isSame(endOfMonth, 'day')) {
      monthItems.push(item);
    } else if (itemDate.isAfter(endOfMonth)) {
      laterItems.push(item);
    }
  });

  return {
    today: todayItems,
    week: weekItems,
    month: monthItems,
    later: laterItems,
  };
}
